// Computes the monthly upsell proposal — the dollar/€/¥/etc. amount
// we suggest when nudging a one-time donor to convert to a recurring
// monthly. Manager-decided UX (2026-06-01): the proposal should be
// smaller than the one-time donor's choice whenever a smaller valid
// amount exists; when the one-time is already at the partner's
// minimum (no room to step down), the proposal equals the one-time
// and we still show the upsell so the donor sees the monthly nudge.
//
// Algorithm:
//   1. target = oneTime × 0.25 — quarter-of-one-time baseline, which
//      gives a monthly that totals roughly 3× the one-time over a
//      year. Conservative enough to feel like a continuation of the
//      donor's original intent, aggressive enough to be worth asking.
//   2. Snap target UP to a "nice" number via magicCeil (port of
//      `magic_round` from lib/tasks/pwc_currency_amounts.rake —
//      adaptive stepping that works for any currency without per-
//      currency hardcoded steps).
//   3. Clamp the result to [minAmount, oneTime] (inclusive both ends)
//      — when one-time is at the minimum, the only valid proposal IS
//      the minimum (= one-time), and that's still a useful upsell to
//      show. Ceil can also push the proposal above oneTime when target
//      is just under a step boundary (e.g., oneTime=$40 → target=$10
//      → ceil=$10; but oneTime=$50 → target=$12.50 → ceil=$15 < $50,
//      fine; oneTime=$2 → target=$0.50 → ceil=$5 → clamps down to $2).
//      Returns null only if oneTime ≤ 0.

const PROPOSAL_RATIO = 0.25

// Returns the "nice" rounding step for an amount. Same shape as the
// Ruby magic_round in lib/tasks/pwc_currency_amounts.rake: doubling
// the digit count grows the step by 10× (length 1-2 → step 5, 3-4 →
// 50, 5-6 → 500, …). Scales by magnitude so $5 / $500 / ¥500 / ¥50,000
// each pick an appropriate granularity without per-currency branching.
const magicStep = amount => {
  if (amount <= 0) return 1
  const length = Math.floor(amount).toString().length
  const magicLength = Math.floor(length * 0.45)
  return 5 * Math.pow(10, magicLength)
}

// Snaps an amount UP to the nearest multiple of magicStep(amount).
// Manager-decided 2026-06-15: ceil over floor as a small upward bias
// on the proposed monthly. The "strictly smaller than one-time"
// guarantee is preserved by the Math.min(proposed, oneTime) clamp at
// the bottom of proposeMonthlyUpsell, not by the rounding direction.
const magicCeil = amount => {
  const step = magicStep(amount)
  return Math.ceil(amount / step) * step
}

const proposeMonthlyUpsell = ({ oneTime, minAmount }) => {
  if (oneTime <= 0) return null

  let proposed = magicCeil(oneTime * PROPOSAL_RATIO)

  // Clamp to validity. The min is rounded UP to whole so non-USD
  // converted minimums (e.g., €0.92 from a $1 USD baseline) don't
  // surface fractional proposals. effectiveMin caps at one-time so
  // a tiny one-time below the rounded-up min still lands a sensible
  // proposal (= oneTime) rather than nudging the donor above their
  // own choice.
  const wholeMin = Math.ceil(minAmount)
  const effectiveMin = Math.min(wholeMin, oneTime)
  proposed = Math.max(proposed, effectiveMin)
  proposed = Math.min(proposed, oneTime)

  return proposed > 0 ? proposed : null
}

export default proposeMonthlyUpsell
